import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { StringField } from "../ConfigFields/StringField";
import typography from "../../../../cssUtils/typography";
import { useAuth } from "../../../../hooks/AuthProvider";
import { FORMS_FORMS, FORMS_FORM_EDIT } from "../../../../config/urls";
import { ColorsConstants } from "../../../../cssUtils/constants";
import { flattenElements } from "../../../Forms/Components/utils";

// Field types whose submitted value is worth mapping into a named Lead column
// (static/display-only elements like TextHeading/Footer/Image carry no name).
const MAPPABLE_ELEMENT_TYPES = [
  "TextInput",
  "TextArea",
  "RadioButtonsGroup",
  "CheckboxGroup",
  "Dropdown",
  "OptIn",
];

// LEAD_GENERATION forms only: the only fields configurable from the form.
// Source is always fixed to "WhatsApp Chatbot", Status to "New Lead", and
// Owner is always left blank for leads captured this way — not configurable.
const LEAD_MAPPING_TARGETS = [
  { value: "", label: "Not mapped" },
  { value: "first_name", label: "Name" },
  { value: "phone", label: "WhatsApp Number" },
  { value: "email", label: "Email" },
  { value: "company", label: "Company" },
  { value: "description", label: "Description" },
];

const getMappableFields = (formJson) => {
  const fields = [];
  (formJson?.screens || []).forEach((screen) => {
    flattenElements(screen?.layout?.children?.[0]?.children).forEach((element) => {
      if (element?.name && MAPPABLE_ELEMENT_TYPES.includes(element.type)) {
        fields.push({
          name: element.name,
          label: element.label || element.name,
          screenTitle: screen.title || screen.id,
        });
      }
    });
  });
  return fields;
};

const LeadgenFormPreset = ({ schema, config, handleConfigChange }) => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem("tenant");

  const [forms, setForms] = useState([]);
  const [formsLoading, setFormsLoading] = useState(true);
  const [formsError, setFormsError] = useState("");

  const [selectedFormDetail, setSelectedFormDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [fieldMapping, setFieldMapping] = useState({});
  const [mappingError, setMappingError] = useState("");

  useEffect(() => {
    const load = async () => {
      setFormsLoading(true);
      setFormsError("");
      try {
        const token = await getValidToken();
        const response = await fetch(
          `${FORMS_FORMS}?limit=999&status=PUBLISHED&category=LEAD_GENERATION`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              "X-Tenant-Id": tenant,
            },
          },
        );
        if (!response.ok) throw new Error("Failed to load forms");
        const data = await response.json();
        setForms(data.results || []);
      } catch (err) {
        setFormsError("Could not load forms. Please try again.");
      } finally {
        setFormsLoading(false);
      }
    };
    load();
  }, []);

  const formId = config["form_id"];

  useEffect(() => {
    if (!formId) {
      setSelectedFormDetail(null);
      setFieldMapping({});
      return;
    }
    const loadDetail = async () => {
      setDetailLoading(true);
      setMappingError("");
      try {
        const token = await getValidToken();
        const response = await fetch(`${FORMS_FORMS}${formId}/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        });
        if (!response.ok) throw new Error("Failed to load form details");
        const data = await response.json();
        setSelectedFormDetail(data);
        setFieldMapping(data?.field_mapping || {});
      } catch (err) {
        setMappingError("Could not load field mapping. Please try again.");
      } finally {
        setDetailLoading(false);
      }
    };
    loadDetail();
  }, [formId]);

  const selectedForm =
    forms.find((f) => f.id === config["form_id"]) || null;

  const mappableFields = getMappableFields(selectedFormDetail?.form_json);

  const handleFieldMappingChange = async (fieldName, bbColumn) => {
    const nextMapping = { ...fieldMapping };
    if (bbColumn) {
      nextMapping[fieldName] = bbColumn;
    } else {
      delete nextMapping[fieldName];
    }

    const previousMapping = fieldMapping;
    setFieldMapping(nextMapping);
    setMappingError("");

    try {
      const token = await getValidToken();
      const response = await fetch(FORMS_FORM_EDIT(selectedFormDetail.id), {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
        body: JSON.stringify({
          ...selectedFormDetail,
          field_mapping: nextMapping,
        }),
      });
      if (!response.ok) throw new Error("Failed to save field mapping");
      const data = await response.json();
      setSelectedFormDetail(data);
    } catch (err) {
      setFieldMapping(previousMapping);
      setMappingError("Could not save field mapping. Please try again.");
    }
  };

  return (
    <Stack spacing={3}>
      {/* Form selector */}
      <Autocomplete
        options={forms}
        value={selectedForm}
        loading={formsLoading}
        onChange={(_, newValue) =>
          handleConfigChange("form_id", newValue?.id ?? null)
        }
        getOptionLabel={(option) => option?.name || ""}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        noOptionsText={
          formsLoading ? "Loading forms…" : "No forms found"
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Form"
            variant="outlined"
            required
            error={!!formsError}
            helperText={formsError || undefined}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {formsLoading && (
                    <CircularProgress color="inherit" size={16} />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            InputLabelProps={{ sx: typography.bodyLarge }}
            inputProps={{
              ...params.inputProps,
              style: typography.bodyLarge,
            }}
          />
        )}
        renderOption={(props, option) => (
          <Stack
            {...props}
            key={option.id}
            direction="column"
            gap={0.25}
            sx={{
              alignItems: "flex-start !important",
              textAlign: "left",
              width: "100%",
            }}
          >
            <Typography sx={{ ...typography.bodyLarge }}>
              {option.name}
            </Typography>
            {option.category && (
              <Typography
                sx={{ ...typography.bodySmall, color: ColorsConstants.primaryGray40 }}
              >
                {option.category
                  .split("_")
                  .map((w) => w[0] + w.slice(1).toLowerCase())
                  .join(" ")}
              </Typography>
            )}
          </Stack>
        )}
      />

      <StringField
        fieldKey="message_body"
        schema={schema?.properties["message_body"] || {}}
        value={config["message_body"]}
        required={false}
        onChange={(val) => handleConfigChange("message_body", val)}
      />

      <StringField
        fieldKey="cta_button_text"
        schema={schema?.properties["cta_button_text"] || {}}
        value={config["cta_button_text"]}
        required={false}
        onChange={(val) => handleConfigChange("cta_button_text", val)}
      />

      {formId && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack>
              <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
                Field Mapping
              </Typography>
              <Typography sx={{ ...typography.bodySmall, color: "#61748C" }}>
                Map submitted fields to Lead columns (Name, Email, Company,
                etc.) so responses show up directly on the Leads table.
                Unmapped fields are still saved with the lead.
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            {mappingError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {mappingError}
              </Alert>
            )}
            {detailLoading ? (
              <Stack alignItems="center" sx={{ py: 2 }}>
                <CircularProgress size={20} />
              </Stack>
            ) : mappableFields.length === 0 ? (
              <Typography sx={{ ...typography.bodySmall, color: "#61748C" }}>
                Add input fields (Text Input, Dropdown, etc.) to a screen to
                map them.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {mappableFields.map((field) => (
                  <Stack
                    key={field.name}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={typography.bodyLarge}>
                        {field.label}
                      </Typography>
                      {/* <Typography
                        sx={{ ...typography.bodySmall, color: "#61748C" }}
                      >
                        {field.screenTitle}
                      </Typography>*/}
                    </Box>
                    <FormControl sx={{ minWidth: 220 }} size="small">
                      <Select
                        value={fieldMapping[field.name] || ""}
                        onChange={(e) =>
                          handleFieldMappingChange(field.name, e.target.value)
                        }
                      >
                        {LEAD_MAPPING_TARGETS.map((target) => (
                          <MenuItem key={target.value} value={target.value}>
                            {target.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                ))}
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>
      )}
    </Stack>
  );
};

export default LeadgenFormPreset;
